import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faX } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';

const getCurrencySymbol = (currency: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'BRL': 'R$',
  };
  return symbols[currency.toUpperCase()] || currency;
};

interface ProfileCardProps {
  id: string;
  title: string;
  experience: string;
  verification: string;
  skills: string[];
  industry: string;
  industries?: string[];
  summary?: string;
  onBookmark: () => void;
  isBookmarked?: boolean;
  profileData?: any; // Full profile data to pass to details page
  onViewProfile?: (profileData: any) => void; // Callback to open drawer
  hideShortlistButton?: boolean; // Hide the shortlist button
  // Optional daily rate display
  showDayRate?: boolean;
  dayRate?: number;
  dayRateCurrency?: string;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  id,
  title,
  experience,
  verification,
  skills,
  industry,
  industries,
  summary,
  onBookmark,
  isBookmarked = false,
  profileData,
  onViewProfile,
  hideShortlistButton = false,
  showDayRate = false,
  dayRate,
  dayRateCurrency,
}) => {
  return (
    <div
      className="rounded-lg p-6 transition-all duration-200 hover:scale-105"
      style={{
        backgroundColor: '#FFFFFF',
        border: `1px solid ${isBookmarked ? '#FF5C3E' : 'rgba(0, 52, 230, 0.25)'}`,
        fontFamily: 'var(--dreamdev-font-family)',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
      }}
      onMouseEnter={(e) => (e.target as HTMLElement).style.borderColor = isBookmarked ? '#FF5C3E' : '#6BCEFF'}
      onMouseLeave={(e) => (e.target as HTMLElement).style.borderColor = isBookmarked ? '#FF5C3E' : 'rgba(0, 52, 230, 0.25)'}
    >
      {/* Two-column layout */}
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        {/* Left column - All content except SEE PROFILE button */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center mb-2 space-y-2 sm:space-y-0">
            <h3
              className="sm:mr-4"
              style={{
                fontSize: '18px',
                color: '#03238C',
                fontWeight: '700',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.04em'
              }}
            >
              {title}
            </h3>
            <div className="flex items-center">
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="w-4 h-4 mr-1"
                style={{ color: '#0034E6' }}
              />
              <span
                style={{
                  color: '#0034E6',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {verification}
              </span>
              {showDayRate && typeof dayRate === 'number' && (
                <div
                  className="ml-4 px-2 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#E9F7FF',
                    color: '#03238C',
                    fontSize: '16px',
                    fontWeight: 700,
                    fontFamily: 'Inter, sans-serif',
                    border: '1px solid #6BCEFF',
                    textAlign: 'right'
                  }}
                >
                  {getCurrencySymbol(dayRateCurrency || 'USD')} {dayRate?.toLocaleString() || '0'}/day
                </div>
              )}
            </div>
          </div>

          {/* <p
            className="text-sm mb-4"
            style={{
              color: '#6B7280',
              fontSize: 'var(--dreamdev-font-size-sm)'
            }}
          >
            {experience}
          </p> */}

          {/* About content */}
          {summary && (
            <div className="mb-4">
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: '#374151',
                  fontSize: 'var(--dreamdev-font-size-sm)',
                  fontFamily: 'var(--dreamdev-font-family)',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {summary}
              </p>
            </div>
          )}

          {/* Industries tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {industries && industries.length > 0 ? (
              industries.map((industry, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: '#CEECFF',
                    color: '#03238C',
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'Source Sans 3, sans-serif'
                  }}
                >
                  {industry}
                </span>
              ))
            ) : (<div></div>)}
          </div>

          {/* ADD TO SHORTLIST button - bottom left */}
          {!hideShortlistButton && (
            <div className="flex justify-start">
              <button
                onClick={onBookmark}
                className="px-4 py-2 text-sm uppercase flex items-center justify-center space-x-2 transition-colors duration-200 w-full sm:w-auto"
                style={{
                  backgroundColor: 'rgba(255, 92, 62, 0.1)',
                  color: '#FF5C3E',
                  fontSize: 'var(--dreamdev-font-size-sm)',
                  fontWeight: '700',
                  borderRadius: '6px',
                  border: '1px solid #FF5C3E',
                  textShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 92, 62, 0.2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 92, 62, 0.1)';
                }}
              >
                <FontAwesomeIcon
                  icon={isBookmarked ? faX : faPlus}
                  className="w-3 h-3"
                />
                <span>{isBookmarked ? 'REMOVE FROM SHORTLIST' : 'ADD TO SHORTLIST'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Right column - Only SEE PROFILE button */}
        <div className="flex justify-start lg:justify-end lg:items-start">
          <button
            onClick={() => onViewProfile?.(profileData)}
            className="px-4 py-2 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200 w-full lg:w-auto"
            style={{
              backgroundColor: '#E9F7FF',
              color: '#03238C',
              fontSize: 'var(--dreamdev-font-size-sm)',
              fontWeight: '700',
              borderRadius: '6px',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.backgroundColor = '#D1EFFF';
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.backgroundColor = '#E9F7FF';
            }}
          >
            <span>SEE PROFILE</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
